import React from 'react';
import DOMPurify from 'dompurify';
import { Link } from 'react-router-dom';
import { ArrowLeft, RotateCcw } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import '../styles/main.css';

const CONTENT = {
  es: {
    title: 'Política de Reembolsos',
    lastUpdated: 'Última actualización: Marzo 2026',
    backToHome: 'Volver al inicio',
    sections: [
      {
        title: '1. Visión General',
        content: `En IllumiDev, nos comprometemos con la satisfacción del cliente. Esta política describe nuestros procedimientos de reembolso para los diferentes tipos de servicios que ofrecemos.`
      },
      {
        title: '2. Depósitos Iniciales',
        content: `**Antes del inicio del trabajo**: Los depósitos son 100% reembolsables si cancela antes de que comencemos el trabajo.

**Después del inicio del trabajo**: Los depósitos no son reembolsables una vez que el trabajo ha comenzado, ya que cubren el tiempo de planificación y recursos asignados.

**Excepción**: Si no podemos comenzar el proyecto dentro del timeline acordado por razones atribuibles a IllumiDev, ofreceremos un reembolso completo del depósito.`
      },
      {
        title: '3. Proyectos en Progreso',
        content: `Si decide cancelar un proyecto en progreso:

• **Trabajo completado**: Pagará por todo el trabajo completado hasta la fecha de cancelación
• **Materiales**: Cualquier costo de materiales o servicios de terceros incurridos no es reembolsable
• **Aviso**: Se requiere aviso por escrito de al menos 7 días
• **Entregables**: Recibirá todos los entregables completados y pagados

Calcularemos el trabajo completado basándonos en los hitos definidos en la propuesta.`
      },
      {
        title: '4. Proyectos Completados',
        content: `Una vez que un proyecto ha sido entregado y aprobado por el cliente:

• Los pagos no son reembolsables
• Ofrecemos un período de corrección de bugs de 30 días
• Los problemas reportados dentro de este período se corrigen sin cargo adicional
• Las solicitudes de cambios fuera del alcance original no son elegibles para reembolso`
      },
      {
        title: '5. Servicios de Retainer/Mensualidades',
        content: `Para servicios de mantenimiento o retainer mensual:

• **Cancelación**: Puede cancelar con 30 días de aviso
• **Reembolso proporcional**: Si cancela a mitad de mes, no hay reembolso del mes en curso
• **Horas no utilizadas**: Las horas de retainer no utilizadas no se trasladan ni reembolsan
• **Renovación automática**: Los servicios se renuevan automáticamente a menos que se cancelen`
      },
      {
        title: '6. Garantía de Satisfacción',
        content: `Si no está satisfecho con nuestro trabajo:

1. **Comuníquese**: Háganos saber específicamente qué no cumple con sus expectativas
2. **Revisiones**: Trabajaremos para resolver el problema dentro de las revisiones incluidas
3. **Mediación**: Si no podemos llegar a un acuerdo, ofrecemos mediación de buena fe

Nuestro objetivo es siempre encontrar una solución que funcione para ambas partes.`
      },
      {
        title: '7. Circunstancias Especiales',
        content: `Consideramos reembolsos en circunstancias excepcionales:

• Emergencias médicas documentadas
• Desastres naturales que afecten su negocio
• Circunstancias de fuerza mayor

Cada caso se evalúa individualmente y requiere documentación apropiada.`
      },
      {
        title: '8. Proceso de Solicitud de Reembolso',
        content: `Para solicitar un reembolso:

1. Envíe su solicitud por escrito a refunds@illumidev.com
2. Incluya su nombre, proyecto y razón de la solicitud
3. Proporcione documentación relevante si aplica
4. Espere nuestra respuesta dentro de 5 días hábiles

Los reembolsos aprobados se procesan dentro de 10-15 días hábiles al método de pago original.`
      },
      {
        title: '9. Exclusiones',
        content: `No se ofrecen reembolsos por:

• Cambios de opinión después de aprobar el trabajo
• Insatisfacción con resultados de negocio (ventas, tráfico, etc.)
• Servicios de terceros contratados en su nombre
• Dominios, hosting o licencias de software adquiridas`
      },
      {
        title: '10. Contacto',
        content: `Para preguntas sobre reembolsos:

**IllumiDev**
Email: refunds@illumidev.com
Website: illumidev.com

Respondemos a todas las solicitudes dentro de 5 días hábiles.`
      }
    ]
  },
  en: {
    title: 'Refund Policy',
    lastUpdated: 'Last updated: March 2026',
    backToHome: 'Back to home',
    sections: [
      {
        title: '1. Overview',
        content: `At IllumiDev, we are committed to customer satisfaction. This policy describes our refund procedures for the different types of services we offer.`
      },
      {
        title: '2. Initial Deposits',
        content: `**Before work begins**: Deposits are 100% refundable if you cancel before we start work.

**After work begins**: Deposits are non-refundable once work has started, as they cover planning time and allocated resources.

**Exception**: If we cannot start the project within the agreed timeline for reasons attributable to IllumiDev, we will offer a full refund of the deposit.`
      },
      {
        title: '3. Projects in Progress',
        content: `If you decide to cancel a project in progress:

• **Completed work**: You will pay for all work completed up to the cancellation date
• **Materials**: Any third-party materials or services costs incurred are non-refundable
• **Notice**: At least 7 days written notice is required
• **Deliverables**: You will receive all completed and paid deliverables

We will calculate completed work based on the milestones defined in the proposal.`
      },
      {
        title: '4. Completed Projects',
        content: `Once a project has been delivered and approved by the client:

• Payments are non-refundable
• We offer a 30-day bug-fix period
• Issues reported within this period are fixed at no additional charge
• Change requests outside the original scope are not eligible for refund`
      },
      {
        title: '5. Retainer/Monthly Services',
        content: `For maintenance or monthly retainer services:

• **Cancellation**: You can cancel with 30 days notice
• **Pro-rata refund**: If you cancel mid-month, there is no refund for the current month
• **Unused hours**: Unused retainer hours do not carry over or get refunded
• **Auto-renewal**: Services auto-renew unless cancelled`
      },
      {
        title: '6. Satisfaction Guarantee',
        content: `If you are not satisfied with our work:

1. **Communicate**: Let us know specifically what doesn't meet your expectations
2. **Revisions**: We will work to resolve the issue within included revisions
3. **Mediation**: If we cannot reach agreement, we offer good-faith mediation

Our goal is always to find a solution that works for both parties.`
      },
      {
        title: '7. Special Circumstances',
        content: `We consider refunds in exceptional circumstances:

• Documented medical emergencies
• Natural disasters affecting your business
• Force majeure circumstances

Each case is evaluated individually and requires appropriate documentation.`
      },
      {
        title: '8. Refund Request Process',
        content: `To request a refund:

1. Submit your request in writing to refunds@illumidev.com
2. Include your name, project, and reason for request
3. Provide relevant documentation if applicable
4. Expect our response within 5 business days

Approved refunds are processed within 10-15 business days to the original payment method.`
      },
      {
        title: '9. Exclusions',
        content: `Refunds are not offered for:

• Change of mind after approving work
• Dissatisfaction with business results (sales, traffic, etc.)
• Third-party services contracted on your behalf
• Domains, hosting, or software licenses purchased`
      },
      {
        title: '10. Contact',
        content: `For questions about refunds:

**IllumiDev**
Email: refunds@illumidev.com
Website: illumidev.com

We respond to all requests within 5 business days.`
      }
    ]
  }
};

const RefundsPage = () => {
  const { language } = useLanguage();
  const t = CONTENT[language];

  return (
    <>
      <Header />
      <main className="legal-page">
        <div className="container">
          <Link to="/" className="back-link">
            <ArrowLeft size={18} /> {t.backToHome}
          </Link>
          
          <div className="legal-header">
            <RotateCcw size={40} className="legal-icon" />
            <h1>{t.title}</h1>
            <p className="legal-updated">{t.lastUpdated}</p>
          </div>
          
          <div className="legal-content">
            {t.sections.map((section, i) => (
              <section key={i} className="legal-section">
                <h2>{section.title}</h2>
                <div className="legal-text">
                  {section.content.split('\n').map((line, j) => (
                    <p key={j} dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(line
                        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                        .replace(/• /g, '&bull; '))
                    }} />
                  ))}
                </div>
              </section>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default RefundsPage;
